import { useEffect, useRef, useCallback } from 'react';
import { notification } from 'antd';
import { useAlertNotifications } from '../../contexts/useAlertNotifications';
import { useEvent } from '../../hooks/useEvent';
import { type EventAlert, notifyBeforeToMs } from '../../api/alertsService';
import { showAlertToast } from './showAlertToast';

/**
 * Round a date to the start of the current minute (seconds = 0, milliseconds = 0)
 * This ensures consistent comparisons since polling happens at round minutes
 */
function roundToMinute(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), 0, 0);
}

/**
 * Get active notification triggers for an alert
 * Returns an array of trigger keys that are currently active (time has passed)
 */
function getActiveTriggers(alert: EventAlert, now: Date): string[] {
  const triggers: string[] = [];
  const startTime = new Date(alert.start_time);
  const endTime = new Date(alert.end_time);
  // Round now to the start of the current minute for consistent comparisons
  const nowRounded = roundToMinute(now);
  
  // Check if show_immediately trigger is active
  if (alert.show_immediately) {
    triggers.push(`${alert.id}:immediate`);
  }
  
  // Check each notify_before entry
  if (alert.notify_before && alert.notify_before.length > 0) {
    for (const entry of alert.notify_before) {
      const ms = notifyBeforeToMs(entry);
      const notifyTime = new Date(startTime.getTime() - ms);
      if (notifyTime <= nowRounded) {
        triggers.push(`${alert.id}:notify:${entry.value}:${entry.unit}`);
      }
    }
  }
  
  // Check if start_time trigger is active (only if notify_on_start is true)
  if (alert.notify_on_start && startTime <= nowRounded) {
    triggers.push(`${alert.id}:start`);
  }
  
  // Check if end_time trigger is active (only if notify_on_end is true)
  if (alert.notify_on_end && endTime <= nowRounded) {
    triggers.push(`${alert.id}:end`);
  }
  
  return triggers;
}

/**
 * Alert Toast Manager
 * Shows notification toasts when alerts become active (due to show_immediately, notify_before, or start_time)
 */
export const AlertToastManager: React.FC = () => {
  const { selectedEvent } = useEvent();
  const { 
    activeAlerts, 
    dismissAlert, 
    acknowledgeTrigger,
    isAlertDismissed,
    isTriggerAcknowledged,
    popupAlertsMuted,
    compactAlerts,
  } = useAlertNotifications();

  const [api, contextHolder] = notification.useNotification();
  
  // Track shown triggers (e.g., "alertId:immediate", "alertId:notify:30:minutes", "alertId:start")
  // This is for the current session only - prevents re-showing on every poll
  const shownTriggersRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  // Don't show for legacy events
  const isNonLegacyEvent = selectedEvent && !selectedEvent.legacy;

  const showAlertNotification = useCallback((alert: EventAlert, triggerKey: string, triggerType: string) => {
    const notificationKey = `alert-${triggerKey}`;

    const duration = alert.requires_acknowledgment
      ? 0
      : alert.severity === 'critical'
        ? 10
        : alert.severity === 'warning'
          ? 7
          : 5;

    let triggerLabel = '';
    if (triggerType.startsWith('notify:')) {
      const parts = triggerType.split(':');
      triggerLabel = ` (${parts[1]} ${parts[2]} before)`;
    } else if (triggerType === 'start') {
      triggerLabel = ' (start time)';
    } else if (triggerType === 'end') {
      triggerLabel = ' (end time)';
    }

    showAlertToast({
      api,
      alert,
      notificationKey,
      compact: compactAlerts,
      duration,
      triggerLabel,
      onAcknowledge: () => {
        acknowledgeTrigger(triggerKey);
        api.destroy(notificationKey);
      },
      onClose: () => {
        if (!alert.requires_acknowledgment) {
          dismissAlert(alert.id);
        } else {
          shownTriggersRef.current.delete(triggerKey);
        }
      },
    });
  }, [api, acknowledgeTrigger, dismissAlert, compactAlerts]);

  // Show notifications for alerts with newly active triggers
  useEffect(() => {
    if (!isNonLegacyEvent || popupAlertsMuted) return;

    const now = new Date();

    // On initial load, mark all current triggers as "shown" so we don't notify on them
    if (initialLoadRef.current) {
      activeAlerts.forEach(alert => {
        const triggers = getActiveTriggers(alert, now);
        triggers.forEach(trigger => shownTriggersRef.current.add(trigger));
      });
      initialLoadRef.current = false;
      return;
    }

    // Check each active alert for new triggers
    activeAlerts.forEach(alert => {
      // Skip dismissed alerts
      if (isAlertDismissed(alert.id)) {
        return;
      }

      // Get all currently active triggers for this alert
      const triggers = getActiveTriggers(alert, now);
      
      // Show notification for triggers we haven't shown yet
      for (const triggerKey of triggers) {
        // Skip if already shown in this session
        if (shownTriggersRef.current.has(triggerKey)) {
          continue;
        }
        
        // Skip if already acknowledged (for requires_acknowledgment alerts)
        if (alert.requires_acknowledgment && isTriggerAcknowledged(triggerKey)) {
          shownTriggersRef.current.add(triggerKey); // Add to session to prevent re-checking
          continue;
        }
        
        shownTriggersRef.current.add(triggerKey);
        
        // Determine trigger type for display
        const triggerType = triggerKey.split(':').slice(1).join(':'); // Remove alertId prefix
        showAlertNotification(alert, triggerKey, triggerType);
        
        // Only show one notification per alert per poll cycle
        // (prevents multiple notifications if multiple triggers become active at once)
        break;
      }
    });
  }, [activeAlerts, isNonLegacyEvent, isAlertDismissed, isTriggerAcknowledged, popupAlertsMuted, showAlertNotification]);

  // Reset shown triggers when event changes
  useEffect(() => {
    shownTriggersRef.current = new Set();
    initialLoadRef.current = true;
  }, [selectedEvent?.circuit_id, selectedEvent?.start_date]);

  return <>{contextHolder}</>;
};

export default AlertToastManager;
